import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Sparkles } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MockCheckoutDialog } from "@/components/MockCheckoutDialog";
import { Button } from "@/components/ui/button";
import { homepageText } from "@/lib/homepageContent";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Pricing() {
  const { user, setUser, settings } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/subscriptions/plan").then(({ data }) => setPlan(data)).catch(() => {}); }, []);

  const price = plan?.price_inr ?? settings?.price_inr ?? 499;
  const freeMode = (plan?.site_mode ?? settings?.site_mode) === "free";
  const isPremium = user && user.effective_tier === "premium";
  const t = homepageText({ ...settings, price_inr: price });

  const onSuccess = (data) => {
    setCheckout(null);
    setUser(data.user);
    toast.success(data.trial ? "Your 7-day trial has started. Maps unlocked!" : "Payment successful. Welcome to Premium!");
    navigate("/map");
  };

  const subscribe = async () => {
    if (!user) return navigate("/register");
    setLoading(true);
    try {
      const { data } = await api.post("/subscriptions/checkout");
      if (data.mode === "razorpay") {
        const ok = await loadRazorpay();
        if (!ok) throw new Error("Could not load Razorpay");
        const rz = new window.Razorpay({
          key: data.key_id, subscription_id: data.subscription_id, name: "CG Land Geoportal", description: "Premium monthly",
          prefill: { name: user.name, email: user.email },
          handler: async (resp) => {
            try { const { data: done } = await api.post(`/subscriptions/checkout/${data.checkout_id}/confirm-razorpay`, resp); onSuccess(done); }
            catch (err) { toast.error(errorMessage(err)); }
          },
        });
        rz.open();
      } else {
        setCheckout(data);
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="pricing-page" className="max-w-6xl mx-auto px-6 py-16">
      <div className="max-w-2xl">
        <p className="data-label mb-3">{t("pricing_kicker")}</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" data-testid="pricing-title">{t("pricing_title")}</h1>
        <p className="mt-4 text-muted-foreground text-lg">{t("pricing_subtitle")}</p>
      </div>

      {freeMode && (
        <div data-testid="free-mode-banner" className="mt-8 rounded-2xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900 p-5 text-sm text-emerald-900 dark:text-emerald-200">
          <strong>Free Mode is on.</strong> The superadmin has opened maps to everyone. Subscriptions are paused — no billing while this lasts.
        </div>
      )}

      <div className="mt-12 grid md:grid-cols-12 gap-6 items-stretch">
        <div className="md:col-span-5 rounded-2xl border bg-card p-8 flex flex-col">
          <p className="data-label">{t("pricing_free_label")}</p>
          <p className="mt-4 font-mono text-4xl font-semibold">{t("pricing_free_price")}</p>
          <p className="text-sm text-muted-foreground mt-1">{t("pricing_free_period")}</p>
          <ul className="mt-8 space-y-3 text-sm flex-1" data-testid="pricing-free-bullets">
            {t.lines("pricing_free_bullets").map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-emerald-600 shrink-0 mt-0.5" />{f}</li>)}
            {t("pricing_free_excluded") && <li className="flex gap-2 text-muted-foreground"><X size={16} className="shrink-0 mt-0.5" />{t("pricing_free_excluded")}</li>}
          </ul>
          <Button asChild variant="outline" data-testid="pricing-free-button" className="mt-8 rounded-full h-11"><a href={user ? "/dashboard" : "/register"}>{user ? "Go to dashboard" : "Create free account"}</a></Button>
        </div>

        <div data-testid="pricing-premium-card" className="md:col-span-7 rounded-2xl topo-bg grain text-white p-8 md:p-10 relative overflow-hidden border border-emerald-500 ring-2 ring-emerald-500/20 flex flex-col">
          <div className="relative z-10 flex flex-col flex-1">
            <div className="flex items-center justify-between">
              <p className="data-label text-emerald-400">{t("pricing_premium_label")}</p>
              <span className="text-xs font-mono px-3 py-1 rounded-full text-white" style={{ background: "var(--terracotta)" }}>{t("pricing_premium_badge")}</span>
            </div>
            <div className="mt-4 flex items-end gap-2"><p data-testid="pricing-premium-price" className="font-mono text-5xl font-semibold">₹{price}</p><p className="text-slate-400 mb-2">{t("pricing_premium_period")}</p></div>
            <p className="text-sm text-slate-300 mt-1">{t("pricing_premium_note")}</p>
            <ul className="mt-8 space-y-3 text-sm flex-1" data-testid="pricing-premium-bullets">
              {t.lines("pricing_premium_bullets").map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />{f}</li>)}
            </ul>
            {isPremium || freeMode ? (
              <Button asChild data-testid="pricing-open-map-button" className="mt-8 rounded-full h-12 bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-semibold"><a href="/map">{freeMode ? "Maps are free right now — open" : "You're premium — open maps"}</a></Button>
            ) : (
              <Button data-testid="subscription-checkout-button" onClick={subscribe} disabled={loading || (user && !user.email_verified)} className="mt-8 rounded-full h-12 font-semibold text-white hover:opacity-90" style={{ background: "var(--terracotta)" }}>
                <Sparkles size={16} className="mr-2" />{loading ? "Preparing checkout…" : user ? (user.trial_used ? t("pricing_cta_subscribe") : t("pricing_cta_trial")) : t("pricing_cta_guest")}
              </Button>
            )}
            {user && !user.email_verified && !freeMode && <p data-testid="pricing-verify-hint" className="mt-3 text-xs text-amber-300">Verify your email from the dashboard before subscribing.</p>}
          </div>
        </div>
      </div>

      <MockCheckoutDialog checkout={checkout} onClose={() => setCheckout(null)} onSuccess={onSuccess} />
    </div>
  );
}
